// ----------------------------------------------------------------------
// <copyright file="Izenda.LicenseCheckerStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { observe, action } from 'mobx';
import databaseSetupDataFetcher from 'fetchers/Izenda.DatabaseSetupDataFetcher';
import systemSettingFetcher from 'fetchers/Izenda.SystemSettingFetcher';
import DatabaseConfiguration from './models/Izenda.DatabaseConfiguration';
import SystemModeSetting from 'logic/models/Izenda.SystemModeSetting';
import licenseInfoStore from './Izenda.LicenseInfoStore';
import uiState from './Izenda.UiState';

/**
 * License Checker Store
 */
export default class LicenseCheckerStore {
  databaseConfiguration = new DatabaseConfiguration();
  licenseInfoStore = licenseInfoStore;
  systemModeSetting = new SystemModeSetting();

  constructor() {
    observe(this.databaseConfiguration, change => {
      const connectionStatus = change.object;
      if (change.name === 'isValidConnection') {
        if (connectionStatus.isValidConnection) {
          this.licenseInfoStore.getLicenseInformation();
          this.licenseInfoStore.getLicenseStatus();
        } else {
          this.licenseInfoStore.licenseInfo.resetLicenseInfo();
        }
      }
    });
  }

  /**
   * Get init data (connection string)
   */
  getInitData() {
    return Promise.all([this.getConnectionInfo(), this.loadSystemSetting()]);
  }

  /**
   * get suppported database type
   */
  getSupportedDatabaseType() {
    return databaseSetupDataFetcher
      .getSupportedDatabaseType()
      .then(response => {
        this.databaseConfiguration.updateSupportedDatabaseType(response);
      });
  }

  /**
   * Test connection string
   */
  testConnection() {
    const connectionInfo = this.databaseConfiguration.connectionInfo;
    return databaseSetupDataFetcher
      .testConnection(connectionInfo.serverTypeId, connectionInfo.connectionString)
      .then(response => {
        response = response || {};
        this.databaseConfiguration.readTestConnectionStatus(response);
        return response.messages;
      });
  }

  /**
   * Setup Data base after test connection successfully
   * @param {any} resetDataInfo
   * @returns {promise}
   */
  setupDatabase(resetDataInfo) {
    const connectionInfo = this.databaseConfiguration.connectionInfo;
    //reset dataInfo
    resetDataInfo && this.databaseConfiguration.resetDataInfo();

    return databaseSetupDataFetcher
      .setupDatabase(connectionInfo.serverTypeId, connectionInfo.connectionString)
      .then(response => {
        this.databaseConfiguration.setValidConnection(response);
        if (response.success){
          this.licenseInfoStore.getLicenseInformation();
          this.licenseInfoStore.getLicenseStatus();
          uiState.increaseDetectChangeVisibleConnection();
          uiState.increaseDetectChangeCount();
        }
        return response;
      });
  }


  /**
   * Get connection info
   * @returns {promise}
   */
  getConnectionInfo() {
    return databaseSetupDataFetcher
      .getConnectionInfo()
      .then(response => {
        this.databaseConfiguration.updateConnectionInfo(response);
        return null;
      });
  }

  /**
   * Save System Setting mode
   * @returns {promise}
   */
  @action
  saveSystemSetting(data) {
    return systemSettingFetcher
      .saveSystemModeSettings(data)
      .then(action(result => {
        result.success && (uiState.systemMode = data.systemMode);
        return result;
      }));
  }

  /**
   * Load System Setting mode
   * @returns {promise}
   */
  loadSystemSetting() {
    return systemSettingFetcher
      .loadSystemModeSettings()
      .then(this.systemModeSetting.readSystemModeSetting.bind(this.systemModeSetting));
  }
}



// WEBPACK FOOTER //
// ./ui/logic/Izenda.LicenseCheckerStore.js